import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useGlobalContext } from 'GlobalContext';
import AppStoreQR from '../../assets/app-store-qr.png';
import GooglePlayQR from '../../assets/google-play-qr.png';
import '../../style/scss/dashboard.scss';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useModal } from 'components/modals/ModalContext';
import WarningModal from 'components/modals/WarningModal';
import OnboardingVideos from './OnboardingVideos';
import { fetchData } from 'services/api/fetchData';
import { deleteData } from 'services/api/deleteData';
import { getTrialRemainingDays } from 'services/utils/dateTimeUtils';

const TrialHomePage: React.FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { setShowFooterLogo } = useGlobalContext();
    const { initializeModal } = useModal();
    const { subscriptionEndDate, activeFeatures, handleLogout } = useAuthContext();
    const [activeView, setActiveView] = useState<'home' | 'videos'>('home');
    const [demoDataStatus, setDemoDataStatus] = useState<string | null>(null);
    const [demoDataProgress, setDemoDataProgress] = useState<number>(0);
    const [deletedInSession, setDeletedInSession] = useState<boolean>(false);
    const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Show footer logo on mount
    useEffect(() => {
        setShowFooterLogo(true);
    }, []);

    // Fetch the demo data status on mount
    useEffect(() => {
        const fetchDemoDataStatus = async () => {
            try {
                // Fetch the current demo data status
                const response = await fetchData({ apiUrl: 'get_trial_demo_data_status', handleLogout });
                if (response?.status) {
                    setDemoDataStatus(response.status);
                    setDemoDataProgress(response.progress || 0);

                    // Resume polling when a deletion is already running
                    if (response.status === 'deleting') {
                        startDeletionPolling();
                    }
                }
            } catch (error) {
                console.error('Fetching demo data status failed', error);
            }
        };
        fetchDemoDataStatus();

        // Stop polling when leaving the page
        return () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
            }
        };
    }, []);

    // Poll the demo data status while the deletion is running
    const startDeletionPolling = () => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
        }
        pollingRef.current = setInterval(async () => {
            try {
                // Fetch the current demo data status
                const response = await fetchData({ apiUrl: 'get_trial_demo_data_status', handleLogout });
                setDemoDataStatus(response.status);
                setDemoDataProgress(response.progress || 0);

                // Stop polling when the deletion is finished or failed
                if (response.status !== 'deleting') {
                    if (pollingRef.current) {
                        clearInterval(pollingRef.current);
                        pollingRef.current = null;
                    }
                    if (response.status === 'deleted') {
                        setDeletedInSession(true);
                    }
                }
            } catch (error) {
                console.error('Fetching demo data status failed', error);
            }
        }, 1500);
    };

    // Start the demo data deletion in the backend
    const handleDeleteDemoData = async () => {
        try {
            // Call the backend to start the deletion of the demo data
            const response = await deleteData({ apiUrl: 'delete_trial_demo_data' });
            if (response) {
                setDemoDataStatus('deleting');
                setDemoDataProgress(0);
                startDeletionPolling();
            }
        } catch (error) {
            console.error('Starting demo data deletion failed', error);
        }
    };

    // Open the confirmation modal before deleting the demo data
    const openDeleteDemoDataModal = () => {
        initializeModal(
            <WarningModal
                header='dashboard.trial_home.delete_demo_data_confirm_title'
                message='dashboard.trial_home.delete_demo_data_confirm_message'
                buttonLabel='dashboard.trial_home.delete_demo_data_confirm_button'
                onApproval={handleDeleteDemoData}
            />,
            { modalSize: 'extra-small' }
        );
    };

    // Render the demo data deletion card based on the current status
    const renderDemoDataCard = () => {

        // Show the deletion progress while the demo data is being deleted
        if (demoDataStatus === 'deleting') {
            return (
                <div className='extra-card'>
                    <h6>🧹 {t('dashboard.trial_home.delete_demo_data_title')}</h6>
                    <p>{t('dashboard.trial_home.delete_demo_data_progress_label', { progress: demoDataProgress })}</p>
                </div>
            );
        }

        // Show the completed state when the deletion just finished
        if (deletedInSession && demoDataStatus === 'deleted') {
            return (
                <div className='extra-card'>
                    <h6>✅ {t('dashboard.trial_home.delete_demo_data_title')}</h6>
                    <p>{t('dashboard.trial_home.delete_demo_data_done_message')}</p>
                </div>
            );
        }

        // Show the clickable card while demo data is present
        if (demoDataStatus === 'created' || demoDataStatus === 'failed') {
            return (
                <div className='extra-card' onClick={openDeleteDemoDataModal}>
                    <h6>🧹 {t('dashboard.trial_home.delete_demo_data_title')}</h6>
                    <p>{t('dashboard.trial_home.delete_demo_data_description')}</p>
                </div>
            );
        }

        return null;
    };

    return (
        <div className='container-filled'>
            <div className='trial-home'>

                {activeView === 'home' && (
                    <>
                        <div className='trial-header'>
                            <h2>{t('dashboard.trial_home.welcome_title')}</h2>
                            <p>{t('dashboard.trial_home.prefilled_message')}</p>
                            <p>{t('dashboard.trial_home.see_how_it_works_message')}</p>
                        </div>

                        <div className='trial-grid'>

                            <div className='trial-steps'>
                                <h3>{t('dashboard.trial_home.get_started_title')}</h3>

                                <div className='step-cards'>
                                    <div
                                        className='step-card'
                                        onClick={() => history.push(activeFeatures.includes('scheduling') ? '/scheduling' : '/calendar')}
                                    >
                                        <div className='step-left'>
                                            <div className='step-number'>1</div>
                                            <div className='step-text'>
                                                <h6>{t('dashboard.trial_home.view_schedule_title')}</h6>
                                                <p>{t('dashboard.trial_home.view_schedule_description')}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className='step-card'>
                                        <div className='step-left'>
                                            <div className='step-number'>2</div>
                                            <div className='step-text'>
                                                <h6>{t('dashboard.trial_home.view_job_title')}</h6>
                                                <p>{t('dashboard.trial_home.view_job_description')}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className='step-card'>
                                        <div className='step-left'>
                                            <div className='step-number'>3</div>
                                            <div className='step-text'>
                                                <h6>{t('dashboard.trial_home.fill_job_via_app_title')}</h6>
                                                <p>{t('dashboard.trial_home.fill_job_via_app_description')}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        className='step-card'
                                        onClick={() => history.push(activeFeatures.includes('scheduling') ? '/scheduling' : '/calendar')}
                                    >
                                        <div className='step-left'>
                                            <div className='step-number'>4</div>
                                            <div className='step-text'>
                                                <h6>{t('dashboard.trial_home.create_first_job_title')}</h6>
                                                <p>{t('dashboard.trial_home.create_first_job_description')}</p>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>

                            <div className='trial-app-card'>

                                <h6>{t('dashboard.trial_home.download_app_title')}</h6>

                                <p>{t('dashboard.trial_home.download_app_description')}</p>

                                <div className='qr-row'>
                                    <div className='qr-block'>
                                        <img src={AppStoreQR} alt="App Store QR code" className='qr-placeholder'/>
                                        <p>iOS</p>
                                    </div>

                                    <div className='qr-block'>
                                        <img src={GooglePlayQR} alt="Google Play QR code" className='qr-placeholder'/>
                                        <p>Android</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className='trial-extra-cards'>

                            {/* <div
                                className='extra-card'
                                onClick={() => setActiveView('videos')}
                            >
                                <h6>🎥 Bekijk video’s</h6>
                                <p>Leer snel hoe alles werkt</p>
                            </div> */}

                            {renderDemoDataCard()}

                            <div className='extra-card'>
                                <h6>💬 {t('dashboard.trial_home.help_title')}</h6>
                                <p>{t('dashboard.trial_home.call_us_label')} 020 - 261 72 67</p>
                            </div>

                        </div>

                        <div className='trial-banner'>
                            <p>{t('dashboard.trial_home.days_remaining_prefix')} {subscriptionEndDate ? getTrialRemainingDays(subscriptionEndDate) : '0'} {t('dashboard.trial_home.days_remaining_suffix')}</p>
                            <span className='bullit'> • </span>
                            <div
                                className='cta-button'
                                onClick={() => history.push(`/settings/billing/subscription/wizard/1`)}
                            >
                                {t('dashboard.trial_home.order_subscription_cta')}
                            </div>
                        </div>
                    </>
                )}

                {activeView === 'videos' && (
                    <OnboardingVideos onBack={() => setActiveView('home')} />
                )}

            </div>
        </div>
    );
};

export default TrialHomePage;
